import { useState, useCallback, useRef, useLayoutEffect } from 'react';

interface UseListViewSelectionProps {
  items: { id: string }[];
  isLoading: boolean;
  /**
   * Deep-linked id to prefer over `items[0]` whenever the hook would
   * otherwise default to the first row. Re-applied on every such default,
   * not just once on mount, so it survives repeat reload cycles.
   */
  preferredId?: string | null;
}

// preferredId always wins when present, even if it isn't among the currently
// loaded items -- the detail panel fetches by id independently of this list.
const resolveDefaultId = (
  items: { id: string }[],
  preferredId?: string | null
) => preferredId ?? items[0].id;

const useListViewSelection = ({
  items: allItems,
  isLoading,
  preferredId: rawPreferredId
}: UseListViewSelectionProps) => {
  // A deleted id stays in `items` until the caller's reload lands, and a
  // deep-linked `preferredId` outlives it entirely — excluding both keeps the
  // default selection from re-picking a record the API now 404s on.
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const items = deletedIds.length
    ? allItems.filter(item => !deletedIds.includes(item.id))
    : allItems;
  const preferredId =
    rawPreferredId && !deletedIds.includes(rawPreferredId)
      ? rawPreferredId
      : null;

  const [selectedId, setSelectedId] = useState<string | null>(
    items.length > 0 || preferredId
      ? resolveDefaultId(items, preferredId)
      : null
  );

  const prevLoadingRef = useRef(isLoading);
  // preferredId can arrive a render after mount (useDeepLinkSelection reads
  // it in an effect); track it so its arrival alone also triggers applying it.
  const prevPreferredIdRef = useRef(preferredId);

  // A layout effect so the default selection is applied before the browser
  // paints the render in which loading finished; with a passive effect the
  // detail panel briefly showed its "no results" state before the first row
  // was selected.
  useLayoutEffect(() => {
    if (items.length === 0 && !preferredId) {
      setSelectedId(null);
      return;
    }

    const loadingJustFinished = prevLoadingRef.current && !isLoading;
    const preferredIdJustResolved =
      !!preferredId && preferredId !== prevPreferredIdRef.current;
    if (loadingJustFinished || !selectedId || preferredIdJustResolved) {
      setSelectedId(resolveDefaultId(items, preferredId));
    }

    prevLoadingRef.current = isLoading;
    prevPreferredIdRef.current = preferredId;
  }, [items, selectedId, isLoading, preferredId]);

  const handleSelect = useCallback((id: string | null) => {
    setSelectedId(id);
  }, []);

  const handleItemDeleted = useCallback(
    (deletedId: string, onDeletedCallback?: (id: string) => void) => {
      const deletedIndex = items.findIndex(item => item.id === deletedId);
      let nextSelectedId: string | null = null;

      if (deletedIndex !== -1 && items.length > 1) {
        if (deletedIndex < items.length - 1) {
          nextSelectedId = items[deletedIndex + 1].id;
        } else if (deletedIndex > 0) {
          nextSelectedId = items[deletedIndex - 1].id;
        }
      }

      setDeletedIds(prev => [...prev, deletedId]);
      setSelectedId(nextSelectedId);
      onDeletedCallback?.(deletedId);
    },
    [items]
  );

  return {
    selectedId,
    setSelectedId,
    handleSelect,
    handleItemDeleted
  };
};

export default useListViewSelection;
